import { type ComponentProps, mergeProps, Show } from "solid-js";

/**
 * Nova — the Aula mascot 🌟
 *
 * A four-pointed spark-star, warm and curious: embodies the "aha!" of
 * learning and achievement (XP/badges are stars). Theme-aware (Aula's
 * grape→berry gradient, legible in light and dark) with expressions to give
 * personality to celebrations, empty states, and live feedback.
 *
 * `mood`:
 *  - happy      → relaxed smile (default)
 *  - celebrate  → joyful closed eyes! (lesson completion, badge)
 *  - think      → raised eyebrow + gaze (empty states, "nothing here yet…")
 *  - wave       → friendly wink (welcome)
 *  - sleep      → resting (offline / no data)
 */
export type MascotMood = "happy" | "celebrate" | "think" | "wave" | "sleep";

let novaCounter = 0;

interface MascotProps extends ComponentProps<"svg"> {
  mood?: MascotMood;
}

export function Mascot(rawProps: MascotProps) {
  const props = mergeProps({ mood: "happy" as MascotMood }, rawProps);
  const gradId = `nova-grad-${(novaCounter += 1)}`;
  const cheekId = `nova-cheek-${novaCounter}`;

  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Nova, la mascota de Aula"
      {...props}
    >
      <defs>
        <linearGradient id={gradId} x1="10" y1="6" x2="54" y2="58" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.62 0.232 322)" />
          <stop offset="0.55" stop-color="oklch(0.64 0.22 350)" />
          <stop offset="1" stop-color="oklch(0.68 0.2 24)" />
        </linearGradient>
        <radialGradient id={cheekId} cx="0.5" cy="0.5" r="0.5">
          <stop stop-color="oklch(0.72 0.2 20 / 55%)" />
          <stop offset="1" stop-color="oklch(0.72 0.2 20 / 0%)" />
        </radialGradient>
      </defs>

      {/* Cuerpo — estrella de 4 puntas suavizada */}
      <path
        d="M32 3.5c2 6.9 4.3 11.7 8.1 15.5s8.6 6.1 15.5 8.1c-6.9 2-11.7 4.3-15.5 8.1s-6.1 8.7-8.1 15.6c-2-6.9-4.3-11.8-8.1-15.6s-8.6-6.1-15.5-8.1c6.9-2 11.7-4.3 15.5-8.1S30 10.4 32 3.5Z"
        fill={`url(#${gradId})`}
      />

      {/* Mejillas */}
      <circle cx="24" cy="30.5" r="4.2" fill={`url(#${cheekId})`} />
      <circle cx="40" cy="30.5" r="4.2" fill={`url(#${cheekId})`} />

      {/* Ojos + boca por humor */}
      <Show when={props.mood === "happy" || props.mood === "wave"}>
        <circle cx="26.5" cy="26.5" r="2.4" fill="white" />
        <Show when={props.mood === "happy"}>
          <circle cx="37.5" cy="26.5" r="2.4" fill="white" />
        </Show>
        <Show when={props.mood === "wave"}>
          {/* guiño */}
          <path
            d="M35 26.5c1.1-1 2.9-1 4 0"
            stroke="white"
            stroke-width="2.2"
            stroke-linecap="round"
          />
        </Show>
        <path
          d="M27 32.5c2.6 2.4 6.4 2.4 9 0"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          fill="none"
        />
      </Show>

      <Show when={props.mood === "celebrate"}>
        {/* ojos felices ^ ^ + boca abierta sonriente */}
        <path
          d="M23.6 27.4c1-1.4 2.4-2.2 3.6-2.2s2.6.8 3.6 2.2"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          fill="none"
        />
        <path
          d="M33.2 27.4c1-1.4 2.4-2.2 3.6-2.2s2.6.8 3.6 2.2"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          fill="none"
        />
        <path d="M27 31.5c2.8 3.6 7.2 3.6 10 0Z" fill="white" />
      </Show>

      <Show when={props.mood === "think"}>
        <circle cx="26.5" cy="27" r="2.4" fill="white" />
        <circle cx="37.5" cy="27" r="2.4" fill="white" />
        {/* ceja pensativa + boca pequeña de lado */}
        <path
          d="M23.5 22.5c1.6-0.9 3.4-0.9 5 0"
          stroke="white"
          stroke-width="1.8"
          stroke-linecap="round"
        />
        <path
          d="M30.5 33.5c1.6 1 3.4 1 5 0.4"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          fill="none"
        />
      </Show>

      <Show when={props.mood === "sleep"}>
        <path
          d="M23.5 27.5c1.5-1.2 3.5-1.2 5 0"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          fill="none"
        />
        <path
          d="M35.5 27.5c1.5-1.2 3.5-1.2 5 0"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          fill="none"
        />
        <path
          d="M29 33c2 1.6 4 1.6 6 0"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          fill="none"
        />
        <text x="45" y="20" font-size="9" fill="white" opacity="0.85">
          z
        </text>
      </Show>
    </svg>
  );
}
